import React from 'react';
import { motion } from 'framer-motion';
import { FiMessageCircle, FiUsers, FiZap, FiShield, FiBarChart, FiAward, FiCheckCircle } from 'react-icons/fi';

const WhatsAppMarketingPage: React.FC = () => {
  const features = [
    {
      icon: FiMessageCircle,
      title: 'Broadcast Campaigns',
      description: 'Send targeted messages to thousands of customers with personalized WhatsApp broadcasts and campaigns.',
    },
    {
      icon: FiUsers,
      title: 'Audience Segmentation',
      description: 'Segment your audience based on behavior, demographics, and engagement for highly targeted messaging.',
    },
    {
      icon: FiZap,
      title: 'Automated Responses',
      description: 'Set up intelligent chatbots and automated replies to engage customers 24/7 without manual intervention.',
    },
    {
      icon: FiShield,
      title: 'GDPR Compliance',
      description: 'All campaigns are fully compliant with WhatsApp policies and data protection regulations.',
    },
    {
      icon: FiBarChart,
      title: 'Performance Analytics',
      description: 'Track message delivery, open rates, click-through rates, and conversion metrics in real-time.',
    },
    {
      icon: FiAward,
      title: 'Rich Media Support',
      description: 'Send images, videos, documents, and interactive buttons to create engaging customer experiences.',
    },
  ];

  const whatIsWhatsAppMarketing = [
    {
      title: 'Direct Customer Access',
      description: 'WhatsApp has 98% open rates compared to 20% for email, ensuring your messages are seen.',
    },
    {
      title: 'Conversational Commerce',
      description: 'Enable seamless buying experiences with product catalogs and payment integrations right in chat.',
    },
    {
      title: 'Customer Service Excellence',
      description: 'Provide instant support and build stronger customer relationships through personalized conversations.',
    },
    {
      title: 'Cost-Effective Marketing',
      description: 'Reach customers at a fraction of the cost of traditional advertising with higher engagement rates.',
    },
  ];

  const pricingPlans = [
    {
      name: 'Starter',
      price: '₹12,000',
      period: '/month',
      description: 'Perfect for small businesses',
      features: [
        'Up to 1,000 contacts',
        '4 broadcast campaigns/month',
        'Basic automation setup',
        'Template message creation',
        'Monthly analytics report',
        'Email support',
      ],
      highlighted: false,
    },
    {
      name: 'Professional',
      price: '₹25,000',
      period: '/month',
      description: 'For growing businesses',
      features: [
        'Up to 5,000 contacts',
        'Unlimited broadcast campaigns',
        'Advanced chatbot automation',
        'Audience segmentation',
        'Rich media campaigns',
        'Weekly analytics reports',
        'WhatsApp Business API access',
        'Priority support',
      ],
      highlighted: true,
    },
    {
      name: 'Enterprise',
      price: '₹50,000',
      period: '/month',
      description: 'For large-scale operations',
      features: [
        'Unlimited contacts',
        'Unlimited campaigns',
        'AI-powered chatbots',
        'Advanced segmentation',
        'Multi-agent support',
        'CRM integration',
        'Custom API integrations',
        'Real-time analytics dashboard',
        'Dedicated account manager',
        '24/7 priority support',
      ],
      highlighted: false,
    },
  ];

  const whyChooseUs = [
    {
      title: 'WhatsApp Business API Experts',
      description: 'Certified partners with deep expertise in leveraging WhatsApp Business API for maximum marketing impact.',
    },
    {
      title: 'Proven ROI',
      description: 'Our clients see average conversion rates of 45-60% from WhatsApp campaigns, far exceeding other channels.',
    },
    {
      title: 'Compliance Focused',
      description: 'We ensure all campaigns follow WhatsApp policies and data protection laws to protect your business.',
    },
  ];

  const faqs = [
    {
      question: 'Is WhatsApp marketing legal and compliant?',
      answer: 'Yes, when done correctly. We ensure all campaigns comply with WhatsApp Business Policy, GDPR, and local regulations. Users must opt-in to receive messages, and we provide easy opt-out mechanisms.',
    },
    {
      question: 'What\'s the difference between WhatsApp Business and WhatsApp Business API?',
      answer: 'WhatsApp Business is a free app for small businesses with basic features. WhatsApp Business API is for medium to large businesses, offering advanced automation, integration capabilities, and higher message limits. We help you set up and manage API access.',
    },
    {
      question: 'How do I get contact numbers for WhatsApp marketing?',
      answer: 'Contacts must opt-in to receive messages. We help you build your list through website forms, QR codes, click-to-chat buttons, and other compliant collection methods. You can also import existing customer contacts who\'ve given permission.',
    },
    {
      question: 'What kind of messages can I send on WhatsApp?',
      answer: 'You can send text, images, videos, documents, voice messages, location, and interactive buttons. For broadcast messages, you need pre-approved message templates. We help create and get approval for your templates.',
    },
    {
      question: 'How quickly can I start WhatsApp marketing?',
      answer: 'For WhatsApp Business, you can start within days. For WhatsApp Business API, setup takes 1-2 weeks including verification and template approval. We handle the entire setup process for you.',
    },
    {
      question: 'Can WhatsApp marketing work for my industry?',
      answer: 'WhatsApp works for virtually all industries - e-commerce, healthcare, education, real estate, hospitality, finance, and more. We customize strategies based on your specific industry and goals.',
    },
    {
      question: 'How do you measure WhatsApp marketing success?',
      answer: 'We track delivery rates, read rates, response rates, click-through rates, conversion rates, and ROI. You get detailed analytics showing exactly how your campaigns perform.',
    },
    {
      question: 'Can I integrate WhatsApp with my existing tools?',
      answer: 'Yes! We integrate WhatsApp with your CRM, e-commerce platform, helpdesk, and other business tools for seamless workflows and better customer data management.',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-emerald-50">
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#25D366]/5 via-transparent to-[#128C7E]/5 z-0" />
        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-[#25D366] to-[#128C7E] text-white px-6 py-2 rounded-full text-sm font-semibold mb-6">
              <FiMessageCircle className="w-4 h-4" />
              WhatsApp Marketing Services
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-[#25D366] via-[#128C7E] to-[#075E54] bg-clip-text text-transparent leading-tight">
              WhatsApp Marketing
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed break-words">
              WhatsApp Marketing helps your business connect with customers in the most direct and personal way. Instead of waiting for website visits or email opens, you reach people instantly on the platform they use every day—WhatsApp. It's the easiest way to send updates, offers, reminders, and product details without any complicated tools.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-gradient-to-r from-[#25D366] to-[#128C7E] text-white px-8 py-4 rounded-xl font-semibold hover:shadow-xl transition-all duration-300"
              >
                Start WhatsApp Marketing
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-white text-[#25D366] px-8 py-4 rounded-xl font-semibold border-2 border-[#25D366] hover:bg-[#25D366] hover:text-white transition-all duration-300"
              >
                View Success Stories
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What is WhatsApp Marketing Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-4 text-gray-900">
              What is WhatsApp Marketing?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Whether you want simple daily communication or whatsapp marketing bulk messaging, WhatsApp gives you fast delivery, higher engagement, and quick responses. Our whatsapp marketing services are designed to help your business talk to customers in a friendly, human way that builds trust and boosts conversions.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                title: 'Bulk Messaging Campaigns',
                description: 'Fast and safe whatsapp marketing bulk sending for promotions, offers, updates, and seasonal announcements—delivered to thousands instantly.',
              },
              {
                title: 'Automated WhatsApp Flows',
                description: 'Set up lead nurturing, reminders, follow-ups, and customer support automation to keep your communication active 24/7.',
              },
              {
                title: 'Message Template Creation',
                description: 'Attractive, high-converting WhatsApp message designs crafted for sales, engagement, and customer retention.',
              },
              {
                title: 'Customer Segmentation & Lists',
                description: 'Smart targeting based on customer behavior, interests, or purchase history—so every message reaches the right audience.',
              },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-2xl hover:shadow-lg transition-all duration-300"
              >
                <h3 className="text-xl font-semibold mb-3 text-gray-900 break-words">
                  {item.title}
                </h3>
                <p className="text-gray-600 break-words">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-green-50 to-emerald-50">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-4 text-gray-900">
              Our WhatsApp Marketing Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Complete WhatsApp marketing solutions to engage customers and drive conversions
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 group"
                >
                  <div className="w-14 h-14 bg-gradient-to-br from-[#25D366] to-[#128C7E] rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <IconComponent className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3 text-gray-900 break-words">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 break-words">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-4 text-gray-900">
              WhatsApp Marketing Pricing
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Flexible plans for businesses of all sizes
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {pricingPlans.map((plan, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.05 }}
                className={`relative p-8 rounded-2xl transition-all duration-300 ${
                  plan.highlighted
                    ? 'bg-gradient-to-br from-[#25D366] to-[#128C7E] text-white shadow-2xl scale-105'
                    : 'bg-white border-2 border-gray-200 shadow-lg hover:shadow-2xl'
                }`}
              >
                {plan.highlighted && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-4 py-1 rounded-full text-sm font-semibold">
                    Most Popular
                  </div>
                )}
                <h3
                  className={`text-2xl font-bold mb-2 ${
                    plan.highlighted ? 'text-white' : 'text-gray-900'
                  }`}
                >
                  {plan.name}
                </h3>
                <div className="mb-4">
                  <span
                    className={`text-4xl font-bold ${
                      plan.highlighted ? 'text-white' : 'text-[#25D366]'
                    }`}
                  >
                    {plan.price}
                  </span>
                  <span
                    className={plan.highlighted ? 'text-green-100' : 'text-gray-600'}
                  >
                    {plan.period}
                  </span>
                </div>
                <p
                  className={`mb-6 ${
                    plan.highlighted ? 'text-green-100' : 'text-gray-600'
                  }`}
                >
                  {plan.description}
                </p>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start">
                      <FiCheckCircle
                        className={`w-5 h-5 mr-2 flex-shrink-0 mt-0.5 ${
                          plan.highlighted ? 'text-white' : 'text-[#25D366]'
                        }`}
                      />
                      <span
                        className={
                          plan.highlighted ? 'text-green-50' : 'text-gray-600'
                        }
                      >
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className={`w-full py-3 rounded-xl font-semibold transition-all duration-300 ${
                    plan.highlighted
                      ? 'bg-white text-[#25D366] hover:bg-green-50'
                      : 'bg-gradient-to-r from-[#25D366] to-[#128C7E] text-white hover:shadow-lg'
                  }`}
                >
                  Get Started
                </motion.button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-green-50 to-emerald-50">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-4 text-gray-900">
              Why Choose Us for WhatsApp Marketing?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              As a trusted agency offering professional WhatsApp marketing services, we focus on creating clear, simple, and high-response communication that feels real—not spammy.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: 'Personal & Relatable Messages',
                description: 'Sending messages that feel personal and relatable while running safe, compliant WhatsApp marketing bulk campaigns.',
              },
              {
                title: 'High-Response Templates',
                description: 'Creating message templates that get replies and clicks, planning WhatsApp flows that nurture leads automatically.',
              },
              {
                title: 'Consistent Communication',
                description: 'Helping you stay consistent with your customer communication and using data and insights to improve every campaign. Our goal is simple: Make your business feel closer to your customers—without forcing anything.',
              },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-[#25D366] bg-opacity-10 rounded-lg flex items-center justify-center mr-4 flex-shrink-0 mt-1">
                    <FiCheckCircle className="w-5 h-5 text-[#25D366]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2 text-gray-900 break-words">
                      {item.title}
                    </h3>
                    <p className="text-gray-600 break-words">{item.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-4 text-gray-900">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600">
              Everything you need to know about WhatsApp marketing
            </p>
          </motion.div>
          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-2xl"
              >
                <h3 className="text-lg font-semibold mb-2 text-gray-900 break-words">
                  {faq.question}
                </h3>
                <p className="text-gray-600 break-words">{faq.answer}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-[#25D366] to-[#128C7E]">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold mb-6 text-white">
              Ready to Transform Your Customer Engagement?
            </h2>
            <p className="text-xl text-green-100 mb-8">
              Start reaching customers with WhatsApp's unmatched open rates and engagement
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-white text-[#25D366] px-8 py-4 rounded-xl font-semibold hover:bg-green-50 transition-all duration-300 shadow-xl"
            >
              Get Started Today
            </motion.button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default WhatsAppMarketingPage;
